import {useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router'
import {useQueryClient, useQuery, useMutation} from 'react-query'
import {FieldArray, FormikProvider, useFormik} from 'formik'
import {toast} from 'react-toastify'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {InputDefault, SelectDefault} from '../../../../components/ui'
import {getPersonaById, putPersona} from '../../../../modules/services/persona.service'
import {getListProduct, putProduct} from '../../../../modules/services/product.service'
import {getBreadCrumbs} from '../../breadcrumbs'
import {initialValueMock} from '../../../../mocks'

const PersonaUpdatePage = () => {
  const {id} = useParams<{id: string}>()
  const queryClient = useQueryClient()
  const {data} = useQuery(['getPersonaById', id], () => getPersonaById(id))
  const {data: products} = useQuery('getListProduct', () => getListProduct())
  const mutation = useMutation(putPersona)
  const productMutation = useMutation(putProduct)
  const selectedProducts = (products?.data as Array<any>)?.filter(
    (product) => product.attributes.personas?.data?.id === parseInt(id as string)
  )

  const formik = useFormik({
    initialValues: {
      ...initialValueMock.persona,
      name: data?.attributes.name || '',
      description: data?.attributes.description || '',
      recommendations: selectedProducts?.map((product) => (product.id as number).toString()) || [],
    },
    onSubmit: (values) => {
      mutation.mutate(
        {id, ...values},
        {
          onSuccess: (data) => {
            values.recommendations.forEach((recommendation) => {
              const product = (products?.data as Array<any>)?.find(
                (product) => product.id === parseInt(recommendation)
              )
              productMutation.mutate({
                id: recommendation,
                form: {
                  personas: data.id,
                  description: product.attributes.description,
                  link: product.attributes.url,
                  name: product.attributes.name,
                  point: product.attributes.point,
                  product_detail: product.attributes.product_detail,
                  sku: product.attributes.sku,
                  transaction_value: product.attributes.transaction_value,
                  globalPercents: product.attributes.globalPercent,
                  parcent: product.attributes.percent,
                },
              })
            })
            toast.success('Update Data Success')
            queryClient.invalidateQueries('getListPersona')
          },
          onError(error: any, variables, context) {
            console.log('Error', error.response?.data)
            toast.error(error.response?.data?.error?.message)
          },
        }
      )
    },
    enableReinitialize: true,
  })

  useEffect(() => {
    document.getElementById('kt_layout_toolbar')?.classList.remove('d-none')
    return () => {
      document.getElementById('kt_layout_toolbar')?.classList.add('d-none')
    }
  }, [])

  return (
    <>
      <div className='card mb-10'>
        <div className='card-body d-flex justify-content-between align-items-center py-8'>
          <div className='ms-6'>
            <p className='fw-bold fs-6 p-0 m-0'>Update Persona</p>
          </div>
        </div>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className='d-flex flex-column align-items-center gap-4 px-8'>
              <div className='w-100'>
                <label htmlFor='name' className='form-label'>
                  Persona
                </label>
                <InputDefault
                  type='text'
                  id='name'
                  name='name'
                  value={formik.values.name}
                  onChange={formik.handleChange}
                />
              </div>
              <div className='w-100'>
                <label htmlFor='description' className='form-label'>
                  Description
                </label>
                <InputDefault
                  type='text'
                  id='description'
                  name='description'
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </div>
              <FieldArray name='recommendations'>
                {({remove, push}) => (
                  <div className='w-100'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <label htmlFor='recommendations' className='form-label'>
                        Product Recommendations
                      </label>
                      <button
                        onClick={() => push('')}
                        type='button'
                        className='btn btn-sm btn-light-primary'
                      >
                        Add
                      </button>
                    </div>
                    <div className='d-flex flex-column gap-4'>
                      {formik.values.recommendations.map((recommendation, index) => (
                        <div key={index} className='d-flex gap-2 w-50'>
                          <SelectDefault
                            id={`recommendations.${index}`}
                            name={`recommendations.${index}`}
                            label='Product Recommendations'
                            options={
                              (products?.data as Array<any>)?.map((product) => ({
                                label: `${product.attributes.sku} - ${product.attributes.name}`,
                                value: product.id,
                              })) || []
                            }
                            value={recommendation}
                            onChange={formik.handleChange}
                          />
                          <button
                            onClick={() => remove(index)}
                            type='button'
                            className='btn btn-sm btn-light-danger'
                          >
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </FieldArray>
            </div>
            <div className='d-flex justify-content-end pt-6 gap-4 p-8'>
              <button
                type='button'
                id='close-modal'
                className='btn btn-light'
                data-bs-dismiss='modal'
              >
                Cancel
              </button>
              <button type='submit' className='btn btn-primary'>
                Submit
              </button>
            </div>
          </form>
        </FormikProvider>
      </div>
    </>
  )
}

const PersonaUpdateWrapper = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={getBreadCrumbs('self-care-plan/personaupdate')}>
        {intl.formatMessage({id: 'MENU.PERSONA'})}
      </PageTitle>
      <PersonaUpdatePage />
    </>
  )
}

export default PersonaUpdateWrapper
